import { useState, type FormEvent } from "react";
import { CatalogImage } from "../components/CatalogImage";
import { storedOrderAccessToken, useCustomerOrder } from "../hooks/useCustomerOrder";
import { moneyFormatter } from "../lib/money";
import "../components/CartPanel.css";
import "./PaymentPage.css";

type CustomerOrderPageProps = {
  orderCode: string;
  onOpenPayment: (orderCode: string) => void;
};

function readableStatus(status: string) {
  return status.replaceAll("_", " ");
}

export function CustomerOrderPage({ orderCode, onOpenPayment }: CustomerOrderPageProps) {
  const { order, refreshOrder, status } = useCustomerOrder({ autoRefresh: true, orderCode });
  const [cancelReason, setCancelReason] = useState("");
  const [actionStatus, setActionStatus] = useState("");

  async function requestCancellation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!order) {
      return;
    }

    setActionStatus("Updating order...");

    const response = await fetch(`/api/orders/${encodeURIComponent(order.orderCode ?? orderCode)}/cancel`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Order-Access-Token": storedOrderAccessToken(order.orderCode ?? orderCode),
      },
      body: JSON.stringify({ reason: cancelReason }),
    });
    const body = await response.json<{ error?: string; status?: string }>();

    if (!response.ok) {
      setActionStatus(body.error ?? "Order could not be updated.");
      return;
    }

    setActionStatus(
      body.status === "cancelled"
        ? "Order cancelled."
        : "Cancellation request sent."
    );
    setCancelReason("");
    await refreshOrder();
  }

  if (status && !order) {
    return (
      <section className="commerce-panel payment-panel" aria-labelledby="order-title">
        <article className="payment-card">
          <p className="eyebrow">Order</p>
          <h1 id="order-title">Order access needed</h1>
          <p>{status}</p>
        </article>
      </section>
    );
  }

  if (!order) {
    return (
      <section className="commerce-panel payment-panel" aria-labelledby="order-title">
        <article className="payment-card">
          <p className="eyebrow">Order</p>
          <h1 id="order-title">Loading order</h1>
          <p>Checking the order reference.</p>
        </article>
      </section>
    );
  }

  const canRequestCancellation = ![
    "cancelled",
    "cancellation_requested",
    "completed",
  ].includes(order.status);
  const cancellationCopy =
    order.paymentStatus === "paid" || order.claimedBy
      ? "This order is already in shop review, so cancellation will be sent as a request."
      : "This order can still be cancelled before the shop starts fulfillment.";

  return (
    <section className="commerce-panel payment-panel" aria-labelledby="order-title">
      <div className="payment-grid">
        <article className="payment-card payment-card--primary">
          <div className="payment-card__heading">
            <div>
              <p className="eyebrow">Order {order.orderCode}</p>
              <h1 id="order-title">{readableStatus(order.status)}</h1>
            </div>
            <span className="role-pill">{readableStatus(order.paymentStatus)}</span>
          </div>

          <dl className="payment-meta payment-meta--wide">
            <div>
              <dt>Total</dt>
              <dd>{moneyFormatter.format(order.totalKes)}</dd>
            </div>
            <div>
              <dt>Items</dt>
              <dd>{order.itemCount}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>{order.phone}</dd>
            </div>
            <div>
              <dt>Fulfillment</dt>
              <dd>{readableStatus(order.fulfillmentMethod)}</dd>
            </div>
          </dl>

          {order.paymentStatus !== "paid" ? (
            <button className="button" type="button" onClick={() => onOpenPayment(order.orderCode ?? orderCode)}>
              Pay order
            </button>
          ) : null}

          {order.status === "cancellation_requested" ? (
            <p className="form-status">Cancellation request received. The shop will review it.</p>
          ) : null}

          {order.status === "cancelled" ? (
            <p className="form-status">This order has been cancelled.</p>
          ) : null}

          {canRequestCancellation ? (
            <form
              className="order-action-form"
              onSubmit={(event) => {
                void requestCancellation(event);
              }}
            >
              <label>
                Cancel order
                <textarea
                  value={cancelReason}
                  onChange={(event) => setCancelReason(event.target.value)}
                  placeholder="Reason, fitment changed, ordered by mistake..."
                />
              </label>
              <p>{cancellationCopy}</p>
              <button className="button button--secondary" type="submit">
                {order.paymentStatus === "paid" || order.claimedBy ? "Request cancellation" : "Cancel order"}
              </button>
            </form>
          ) : null}

          {actionStatus ? <p className="form-status">{actionStatus}</p> : null}
        </article>

        <div className="order-lines">
          {order.items.map((item) => (
            <article className="cart-line cart-line--readonly" key={item.id}>
              <CatalogImage
                className="cart-line__image"
                alt={item.imageAlt}
                preset="thumb"
                storageKey={item.imageStorageKey ?? ""}
              />
              <div className="cart-line__content">
                <p className="eyebrow">{item.category || item.sku}</p>
                <h3>{item.name}</h3>
                <p>{item.sku}</p>
                <dl className="cart-line__meta">
                  <div>
                    <dt>Qty</dt>
                    <dd>{item.qty}</dd>
                  </div>
                  <div>
                    <dt>Price</dt>
                    <dd>{moneyFormatter.format(item.unitPriceKes)}</dd>
                  </div>
                  <div className="cart-line__meta-total">
                    <dt>Line total</dt>
                    <dd>{moneyFormatter.format(item.qty * item.unitPriceKes)}</dd>
                  </div>
                </dl>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
